import { useEffect, useRef, useState, useCallback } from 'react'
import type { Ayah } from '../types'
import { getAudioUrl, RECITERS } from '../lib/reciters'
import {
  PlaybackEngine,
  type PlaybackState,
  type QueueItem,
} from '../lib/audio/playbackEngine'
import { setMetadata, setActionHandlers, setPositionState, setPlaybackState, clearMediaSession } from '../lib/audio/mediaSession'
import { useWakeLock } from './useWakeLock'

type SurahHandoff = {
  surahData: unknown
  surahNumber: number
  ayahNumber: number
}

interface UseAudioPlayerOptions {
  ayahs: Ayah[]
  reciterEdition?: string
  /**
   * Optional per-ayah reciter resolver. When it returns a value, that value
   * wins over `reciterEdition` for THAT ayah's audio URL — used by the
   * playlist player to apply a per-surah-row reciter override without the
   * reader (which never passes this) being affected. Falls back to
   * `reciterEdition` when the resolver is absent or returns a falsy value.
   */
  resolveReciter?: (ayah: Ayah) => string
  /**
   * Fired on every ayah transition. `item` carries the engine's authoritative
   * surah/ayah for the now-playing ayah — preferred over re-indexing the
   * caller's `ayahs` array, which can lag a frame at surah handoffs.
   */
  onAyahChange?: (index: number, item: QueueItem) => void
  onSurahHandoff?: (handoff: SurahHandoff) => void
  fetchNextSurah?: (surahNumber: number) => Promise<{
    ayahs: Ayah[]
    surahData: unknown
    /**
     * Effective reciter for this batch's FIRST ayah, computed by the caller
     * at fetch time. Takes precedence over `resolveReciter`/`reciterEdition`
     * when building that first ayah's URL below — the caller already knows
     * which row this batch belongs to at the moment it resolves this
     * promise, whereas `resolveReciter` would have to infer it later from
     * state that may have moved on by then (e.g. a playlist's "active row"
     * ref, which only updates once this handoff is actually applied).
     */
    reciter?: string
  } | null>
  /** Optional resume target (informational — caller decides whether to seek). */
  initialResumeAt?: { ayahIndex: number; currentTime: number } | null
  /** Called at most once every 5 seconds with the current ayah index and playback time. */
  onTimeTick?: (ayahIndex: number, currentTime: number) => void
}

export function useAudioPlayer({
  ayahs,
  reciterEdition = 'sudais',
  resolveReciter,
  onAyahChange,
  onSurahHandoff,
  fetchNextSurah,
  initialResumeAt: _initialResumeAt, // informational; caller handles actual seek
  onTimeTick,
}: UseAudioPlayerOptions) {
  const ayahsRef = useRef<Ayah[]>(ayahs)
  ayahsRef.current = ayahs
  const reciterRef = useRef(reciterEdition)
  reciterRef.current = reciterEdition
  const resolveReciterRef = useRef(resolveReciter)
  resolveReciterRef.current = resolveReciter
  const onAyahChangeRef = useRef(onAyahChange)
  onAyahChangeRef.current = onAyahChange
  const onSurahHandoffRef = useRef(onSurahHandoff)
  onSurahHandoffRef.current = onSurahHandoff
  const fetchNextSurahRef = useRef(fetchNextSurah)
  fetchNextSurahRef.current = fetchNextSurah
  const onTimeTickRef = useRef(onTimeTick)
  onTimeTickRef.current = onTimeTick
  const lastTickRef = useRef(0)

  // Volume persists across reloads in localStorage so user-chosen level
  // survives navigation. Skip PreferencesContext (overkill for one number).
  const VOLUME_STORAGE_KEY = 'qlearn:volume'
  const initialVolume = (() => {
    if (typeof window === 'undefined') return 1
    try {
      const raw = window.localStorage.getItem(VOLUME_STORAGE_KEY)
      if (raw === null) return 1
      const parsed = parseFloat(raw)
      if (Number.isNaN(parsed)) return 1
      return Math.max(0, Math.min(parsed, 1))
    } catch {
      return 1
    }
  })()

  const [state, setState] = useState<PlaybackState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    currentAyahIndex: 0,
    loading: false,
    error: null,
    volume: initialVolume,
  })
  const [continuousMode, setContinuousMode] = useState(true)
  const continuousRef = useRef(continuousMode)
  continuousRef.current = continuousMode

  const engineRef = useRef<PlaybackEngine | null>(null)

  // Build a queue item from an ayah index in the *current* surah.
  const buildItem = useCallback((index: number): QueueItem | null => {
    const a = ayahsRef.current[index]
    if (!a) return null
    return {
      url: getAudioUrl(resolveReciterRef.current?.(a) ?? reciterRef.current, a),
      index,
      surahNumber: a.surahNumber,
      ayahNumber: a.ayahNumber,
    }
  }, [])

  // Initialize the engine once on mount.
  useEffect(() => {
    const engine = new PlaybackEngine({
      onStateChange: (s) => setState(s),
      onAyahChange: (idx, item) => onAyahChangeRef.current?.(idx, item),
      onSurahHandoff: (item) => {
        if (!item.surahData) return
        // The engine calls scheduleNext SYNCHRONOUSLY right after this handler.
        // queueProvider reads ayahsRef.current to decide the next URL — but the
        // React state update from applySurahData hasn't propagated yet, so
        // ayahsRef still holds the PREVIOUS surah's ayahs. Without this line,
        // the engine would schedule "previous surah, ayah 2" as the next item
        // and play it after the new surah's first ayah.
        const surahData = item.surahData as { ayahs?: Ayah[] }
        if (surahData?.ayahs && surahData.ayahs.length > 0) {
          ayahsRef.current = surahData.ayahs
        }
        onSurahHandoffRef.current?.({
          surahData: item.surahData,
          surahNumber: item.surahNumber ?? 0,
          ayahNumber: item.ayahNumber ?? 1,
        })
      },
      queueProvider: async (currentIndex) => {
        if (!continuousRef.current) return null
        const list = ayahsRef.current
        // Within current surah
        if (currentIndex + 1 < list.length) {
          return buildItem(currentIndex + 1)
        }
        // End of surah → defer to the caller's fetchNextSurah. We deliberately
        // do NOT cap at surahNumber>=114 here: that's the reader's natural-order
        // rule, but a playlist (custom ordering, repeat modes) needs to be able
        // to wrap past 114 or loop. The caller can enforce its own bound by
        // returning null when appropriate (the reader does so by re-fetching
        // surah lastAyah.surahNumber+1 and getting a 4xx).
        const lastAyah = list[list.length - 1]
        if (!lastAyah || !fetchNextSurahRef.current) return null
        const result = await fetchNextSurahRef.current(lastAyah.surahNumber + 1)
        if (!result || result.ayahs.length === 0) return null
        const first = result.ayahs[0]
        return {
          url: getAudioUrl(
            result.reciter || resolveReciterRef.current?.(first) || reciterRef.current,
            first,
          ),
          index: 0,
          surahNumber: first.surahNumber,
          ayahNumber: first.ayahNumber,
          surahData: result.surahData,
        }
      },
    })
    engineRef.current = engine
    // Apply the persisted volume to the engine immediately so the first
    // playAyah() honours it even though the GainNode is built lazily.
    engine.setVolume(initialVolume)

    setActionHandlers({
      play: () => { void engine.resume() },
      pause: () => engine.pause(),
      prev: () => { void engine.prev() },
      next: () => { void engine.next() },
      seek: (t) => engine.seek(t),
    })

    return () => {
      engine.dispose()
      engineRef.current = null
      clearMediaSession()
    }
  }, [buildItem])

  // Whenever current ayah / surah changes, refresh Media Session metadata + position.
  useEffect(() => {
    const a = ayahsRef.current[state.currentAyahIndex]
    if (!a) return
    const reciter = RECITERS.find((r) => r.identifier === reciterRef.current)
    setMetadata({
      title: `Surah ${a.surahNumber}, Ayah ${a.ayahNumber}`,
      artist: reciter?.name ?? reciterRef.current,
      album: 'Qlearn',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ayahsRef holds latest; deliberate dep set
  }, [state.currentAyahIndex])

  useEffect(() => {
    setPlaybackState(state.isPlaying ? 'playing' : 'paused')
  }, [state.isPlaying])

  // Hold a screen wake-lock while audio is playing. On mobile a 45-minute
  // surah otherwise gets cut short the moment the screen auto-locks (iOS
  // backgrounds the page, Android suspends Web Audio). See useWakeLock for
  // the mechanics (feature detection, re-acquire on visibilitychange, etc).
  useWakeLock(state.isPlaying)

  useEffect(() => {
    setPositionState({
      duration: state.duration,
      position: state.currentTime,
      rate: 1,
    })
  }, [state.duration, state.currentTime])

  // Throttled time-tick: fires onTimeTick at most once every 5 seconds.
  useEffect(() => {
    if (!onTimeTickRef.current) return
    const now = Date.now()
    if (now - lastTickRef.current < 5000) return
    lastTickRef.current = now
    onTimeTickRef.current(state.currentAyahIndex, state.currentTime)
  }, [state.currentTime, state.currentAyahIndex])

  // ----- Public API (preserved shape) -----

  const playAyah = useCallback(async (index: number) => {
    const engine = engineRef.current
    if (!engine) return
    const item = buildItem(index)
    if (!item) return
    await engine.play(item)
  }, [buildItem])

  const togglePlay = useCallback(async () => {
    const engine = engineRef.current
    if (!engine) return
    if (state.isPlaying) {
      engine.pause()
      return
    }
    if (state.duration > 0) {
      await engine.resume()
    } else {
      await playAyah(state.currentAyahIndex)
    }
  }, [state.isPlaying, state.duration, state.currentAyahIndex, playAyah])

  const play = togglePlay
  const pause = useCallback(() => engineRef.current?.pause(), [])

  const nextAyah = useCallback(() => {
    void engineRef.current?.next()
  }, [])

  const previousAyah = useCallback(() => {
    void engineRef.current?.prev()
  }, [])

  const seek = useCallback((time: number) => {
    engineRef.current?.seek(time)
  }, [])

  const setVolume = useCallback((value: number) => {
    const clamped = Math.max(0, Math.min(value, 1))
    engineRef.current?.setVolume(clamped)
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(VOLUME_STORAGE_KEY, String(clamped))
      } catch {
        // localStorage may be disabled (private mode); state still updates
      }
    }
  }, [])

  const toggleContinuousMode = useCallback(() => {
    setContinuousMode((p) => !p)
  }, [])

  const reset = useCallback(() => {
    engineRef.current?.pause()
  }, [])

  const currentAyah = ayahs[state.currentAyahIndex] || null

  return {
    isPlaying: state.isPlaying,
    currentTime: state.currentTime,
    duration: state.duration,
    currentAyahIndex: state.currentAyahIndex,
    loading: state.loading,
    error: state.error,
    volume: state.volume,
    continuousMode,
    currentAyah,
    play,
    pause,
    togglePlay,
    playAyah,
    nextAyah,
    previousAyah,
    seek,
    setVolume,
    toggleContinuousMode,
    reset,
  }
}
